// ============================================================================
// Auth Store — eFakt Frontend
// Real JWT-based Zustand store with persist middleware
// Uses createAuthStore factory for React Native compatibility
// ============================================================================

import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";
import type { UserRole } from "../types";
import type { LoginResponse } from "../api-types";
import { ADMIN_ROLES } from "../constants";

// ---------------------------------------------------------------------------
// State Shape
// ---------------------------------------------------------------------------

export interface AuthUser {
  username: string;
  role: UserRole;
  userId: number;
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;

  login: (username: string, password: string, apiBaseUrl: string) => Promise<boolean>;
  logout: () => void;
  hasRole: (requiredRole: UserRole) => boolean;
  isAdmin: () => boolean;
}

// ---------------------------------------------------------------------------
// Role Hierarchy Check
// ---------------------------------------------------------------------------

const ROLE_HIERARCHY: Record<UserRole, number> = {
  "super-admin": 3,
  admin: 2,
  user: 1,
};

function meetsRoleRequirement(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

// ---------------------------------------------------------------------------
// Auth Slice Builder
// ---------------------------------------------------------------------------

type SetState = (partial: Partial<AuthState>) => void;
type GetState = () => AuthState;

function buildAuthSlice(set: SetState, get: GetState): AuthState {
  return {
    user: null,
    token: null,
    isAuthenticated: false,

    login: async (
      username: string,
      password: string,
      apiBaseUrl: string,
    ): Promise<boolean> => {
      try {
        const response = await fetch(`${apiBaseUrl}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
          set({ user: null, token: null, isAuthenticated: false });
          return false;
        }

        const data: LoginResponse = await response.json();

        if (!data.success || !data.token) {
          set({ user: null, token: null, isAuthenticated: false });
          return false;
        }

        const role = (data.role ?? "user") as UserRole;

        // Extract userId from JWT payload
        let userId = 0;
        try {
          const payload = JSON.parse(atob(data.token.split(".")[1]));
          userId = parseInt(payload.userId ?? "0", 10);
        } catch {
          // fallback: userId 0
        }

        set({
          user: { username: data.username ?? username, role, userId },
          token: data.token,
          isAuthenticated: true,
        });

        return true;
      } catch {
        set({ user: null, token: null, isAuthenticated: false });
        return false;
      }
    },

    logout: () => {
      set({ user: null, token: null, isAuthenticated: false });
    },

    hasRole: (requiredRole: UserRole): boolean => {
      const { user } = get();
      if (!user) return false;
      return meetsRoleRequirement(user.role, requiredRole);
    },

    isAdmin: (): boolean => {
      const { user } = get();
      if (!user) return false;
      return ADMIN_ROLES.includes(user.role);
    },
  };
}

// ---------------------------------------------------------------------------
// Default Store (uses localStorage — web)
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => buildAuthSlice(set, get),
    {
      name: "efakt-auth",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

// Cross-tab sync: when another tab changes auth, reload this tab's state
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "efakt-auth") {
      if (e.newValue) {
        try {
          const persisted = JSON.parse(e.newValue);
          const s = persisted?.state;
          if (s) {
            useAuthStore.setState({
              user: s.user ?? null,
              token: s.token ?? null,
              isAuthenticated: s.isAuthenticated ?? false,
            });
          }
        } catch { /* ignore */ }
      } else {
        // Key removed — logout
        useAuthStore.setState({ user: null, token: null, isAuthenticated: false });
      }
    }
  });
}

// ---------------------------------------------------------------------------
// Factory for Custom Storage (React Native AsyncStorage, etc.)
// ---------------------------------------------------------------------------

export function createAuthStore(storage: StateStorage) {
  const store = create<AuthState>()(
    persist(
      (set, get) => buildAuthSlice(set, get),
      {
        name: "efakt-auth",
        storage: createJSONStorage(() => storage),
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
        }),
      },
    ),
  );
  return store;
}
